import { Button, CircularProgress, Grid, Link, TableCell, TableRow, TextField } from '@mui/material';
import { createLaborSubclass, getAllLaborClassifications, getCompanies, getLaborSubclasses, updateLaborSubclass } from 'api';
import { Page, Table } from 'components';
import {
  CompanyDto,
  EquipmentDto,
  equipmentInit,
  LaborClassificationDto,
  LaborSubclassDto,
  LaborSubclassesParameters,
  laborSubclassesParametersInit,
  laborSubclassInit
} from 'dtos';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import AddEditLaborSubclassDialog from './AddEditLaborSubclassDialog';

const LaborSubclasses = () => {
  const { enqueueSnackbar } = useSnackbar();

  const [filterText, setFilterText] = useState<string>('');
  const [isCreatingLaborSubclass, setIsCreatingLaborSubclass] = useState<boolean>(false);
  const [companies, setCompanies] = useState<CompanyDto[]>([]);
  const [laborClassifications, setLaborClassifications] = useState<LaborClassificationDto[]>([]);
  const [isGettingCompanies, setIsGettingCompanies] = useState<boolean>(false);
  const [isGettingLaborClassifications, setIsGettingLaborClassifications] = useState<boolean>(false);
  const [isGettingLaborSubclasses, setIsGettingLaborSubclasses] = useState<boolean>(false);
  const [isUpdatingLaborSubclass, setIsUpdatingLaborSubclass] = useState<boolean>(false);
  const [laborSubclasses, setLaborSubclasses] = useState<LaborSubclassDto[]>([]);
  const [laborSubclassesDialogOpen, setLaborSubclassesDialogOpen] = useState<boolean>(false);
  const [parameters, setParameters] = useState<LaborSubclassesParameters>(laborSubclassesParametersInit);
  const [selectedLaborSubclass, setSelectedLaborSubclass] = useState<LaborSubclassDto>(laborSubclassInit);
  const [totalLaborSubclasses, setTotalLaborSubclasses] = useState<number>(0);

  useEffect(() => {
    getCompanies().then(({ value }) => {
      setCompanies(value);
    });

    getAllLaborClassifications().then(({ value }) => {
      setLaborClassifications(value);
    });
  }, []);

  useEffect(() => {
    onFilter();
  }, [parameters]);

  const onFilter = () => {
    setIsGettingLaborSubclasses(true);
    getLaborSubclasses(parameters)
      .then(({ totalCount, value }) => {
        setTotalLaborSubclasses(totalCount!);
        setLaborSubclasses(value);
      })
      .finally(() => setIsGettingLaborSubclasses(false));
  };

  const onSortChange = (propertyName: string, sortDirection: 'asc' | 'desc') => {
    setParameters({
      ...parameters,
      sortBy: propertyName,
      sortDirection
    });
  };

  const onChangePage = (page: number) => {
    setParameters({
      ...parameters,
      page
    });
  };

  const onChangePageSize = (pageSize: number) => {
    setParameters({
      ...parameters,
      pageSize
    });
  };

  const onReset = () => {
    setParameters({ ...parameters, filterText: '' });
    setFilterText('');
  };

  const onAddLaborSubclass = () => {
    setSelectedLaborSubclass(laborSubclassInit);
    setLaborSubclassesDialogOpen(true);
  };

  const onEditLaborSubclass = (laborSubclass: LaborSubclassDto) => {
    setSelectedLaborSubclass(laborSubclass);
    setLaborSubclassesDialogOpen(true);
  };

  const onCloseLaborSubclassDialog = () => {
    setLaborSubclassesDialogOpen(false);
  };

  const onSaveLaborSubclassDialog = (values: LaborSubclassDto) => {
    if (values.id) {
      setIsUpdatingLaborSubclass(true);
      updateLaborSubclass(values)
        .then(() => {
          onFilter();
          enqueueSnackbar(`${values.name} updated successfully!`, { variant: 'success' });
          onCloseLaborSubclassDialog();
        })
        .finally(() => setIsUpdatingLaborSubclass(false));
    } else {
      setIsCreatingLaborSubclass(true);
      createLaborSubclass(values)
        .then(() => {
          onFilter();
          enqueueSnackbar(`${values.name} created successfully!`, { variant: 'success' });
          onCloseLaborSubclassDialog();
        })
        .finally(() => setIsCreatingLaborSubclass(false));
    }
  };

  return (
    <>
      <AddEditLaborSubclassDialog
        companies={companies}
        isLoading={isCreatingLaborSubclass || isUpdatingLaborSubclass}
        laborClassifications={laborClassifications}
        laborSubclass={selectedLaborSubclass}
        onClose={onCloseLaborSubclassDialog}
        onSave={onSaveLaborSubclassDialog}
        open={laborSubclassesDialogOpen}
      />

      <Page title='Labor Subclasses' isLoading={isGettingLaborSubclasses}>
        <Table
          actions={
            <Grid container justifyContent='space-between'>
              <Grid item xs={12} sm={6} container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label='Filter text'
                    onChange={e => setFilterText(e.target.value)}
                    size='small'
                    value={filterText}
                  />
                </Grid>

                <Grid item>
                  <Button color='inherit' onClick={() => setParameters({ ...parameters, filterText })} variant='contained'>
                    FILTER
                  </Button>
                </Grid>

                <Grid item>
                  <Button color='inherit' onClick={onReset} variant='contained'>
                    RESET
                  </Button>
                </Grid>
              </Grid>

              <Grid item xs={12} sm={6} container justifyContent='flex-end'>
                <Grid item>
                  <Button
                    variant='contained'
                    color='inherit'
                    onClick={onAddLaborSubclass}
                    disabled={isCreatingLaborSubclass || isUpdatingLaborSubclass}
                  >
                    {isCreatingLaborSubclass && (
                      <>
                        <CircularProgress color='inherit' size={20} />
                        &nbsp;&nbsp;
                      </>
                    )}
                    ADD NEW
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          }
          columns={[
            { label: 'Name', propertyName: 'name' },
            { label: 'Labor Classification', propertyName: 'laborClassification' },
            { label: 'Company', propertyName: 'company' }
          ]}
          sortDirection={parameters.sortDirection}
          sortBy={parameters.sortBy}
          onSortChange={onSortChange}
          onChangePage={onChangePage}
          onChangePageSize={onChangePageSize}
          page={parameters.page!}
          pageSize={parameters.pageSize!}
          totalCount={totalLaborSubclasses}
        >
          {laborSubclasses.map(laborSubclass => (
            <TableRow key={laborSubclass.id}>
              <TableCell>
                <Link
                  onClick={() => onEditLaborSubclass(laborSubclass)}
                  sx={{
                    cursor: 'pointer',
                    textDecoration: 'none'
                  }}
                >
                  {laborSubclass.name}
                </Link>
              </TableCell>

              <TableCell>
                {
                  laborClassifications.filter(
                    laborClassification => laborClassification.id === laborSubclass.laborClassificationId
                  )[0]?.name
                }
              </TableCell>

              <TableCell>{companies.filter(company => company.id === laborSubclass.companyId)[0]?.name}</TableCell>
            </TableRow>
          ))}
        </Table>
      </Page>
    </>
  );
};

export default LaborSubclasses;
